import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RequestPresignedUrlResponse,
  requestPresignedUrlContract,
  ConfirmUploadRequest,
  ConfirmUploadResponse,
  confirmUploadContract,
  DeleteAvatarResponse,
  deleteAvatarContract
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import { authKeys, orgMemberKeys } from '../query-keys';
import { toast } from 'sonner';
import { toastApiError } from './toast-api-error';
import i18next from '@/lib/i18n';

/**
 * Request Presigned URL mutation
 *
 * Requests a presigned URL for uploading an avatar directly to DigitalOcean Spaces.
 * This is the first step in the avatar upload flow.
 *
 * @example
 * ```tsx
 * function AvatarUpload() {
 *   const requestUrl = useRequestAvatarUploadUrl();
 *
 *   const handleUpload = async (file: File) => {
 *     try {
 *       const presignedData = await requestUrl.mutateAsync();
 *       // Use presignedData to upload file to S3
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={requestUrl.isPending}>Upload Avatar</Button>;
 * }
 * ```
 */
export function useRequestAvatarUploadUrl() {
  return useMutation({
    mutationFn: async (): Promise<RequestPresignedUrlResponse> => {
      const { data } = await apiClient.post<RequestPresignedUrlResponse>(
        requestPresignedUrlContract.path,
        {}
      );
      return data;
    },
    onError: (error) => {
      toastApiError(error, 'avatar.uploadError');
    }
  });
}

/**
 * Confirm Avatar Upload mutation
 *
 * Confirms the avatar upload and triggers server-side processing.
 * This is the final step after uploading the file to the presigned URL.
 * The server will process the image into multiple sizes and formats.
 *
 * @example
 * ```tsx
 * function AvatarUpload() {
 *   const confirmUpload = useConfirmAvatarUpload();
 *
 *   const handleConfirm = async (key: string) => {
 *     try {
 *       const result = await confirmUpload.mutateAsync({ key });
 *       toast.success('Avatar uploaded successfully');
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={confirmUpload.isPending}>Confirm Upload</Button>;
 * }
 * ```
 */
export function useConfirmAvatarUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ConfirmUploadRequest): Promise<ConfirmUploadResponse> => {
      const { data } = await apiClient.post<ConfirmUploadResponse>(
        confirmUploadContract.path,
        request
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate profile to refetch with new avatar
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });

      // Invalidate org members cache since the current user might appear in that list
      queryClient.invalidateQueries({ queryKey: orgMemberKeys.all });

      toast.success(i18next.t('avatar.uploadSuccess'));
    },
    onError: (error) => {
      toastApiError(error, 'avatar.uploadError');
    }
  });
}

/**
 * Delete Avatar mutation
 *
 * Deletes the user's avatar and all associated files from storage.
 *
 * @example
 * ```tsx
 * function AvatarUpload() {
 *   const deleteAvatar = useDeleteAvatar();
 *
 *   const handleDelete = async () => {
 *     try {
 *       await deleteAvatar.mutateAsync();
 *       toast.success('Avatar deleted successfully');
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={deleteAvatar.isPending}>Delete Avatar</Button>;
 * }
 * ```
 */
export function useDeleteAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DeleteAvatarResponse> => {
      const { data } = await apiClient.delete<DeleteAvatarResponse>(deleteAvatarContract.path);
      return data;
    },
    onSuccess: () => {
      // Invalidate profile to refetch without avatar
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });

      // Invalidate org members cache since the current user might appear in that list
      queryClient.invalidateQueries({ queryKey: orgMemberKeys.all });

      toast.success(i18next.t('avatar.deleteSuccess'));
    },
    onError: (error) => {
      toastApiError(error, 'avatar.deleteError');
    }
  });
}
